// Dashboard marker presentation: metric queries keep the stable marker UUID
// coordinate, while this scoped catalog resolves that coordinate to the current
// display name for authoring and rendered labels (docs/09 §4).

import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";

import type { components } from "../api/schema";
import { apiClient } from "./apiClient";
import { ApiRequestError } from "./errors";
import { MAX_PAGE_SIZE } from "./pagination";
import { queryKeys } from "./queryKeys";
import { useResolvedWorkingTeamId } from "./workingScope";

type MarkerResponse = components["schemas"]["MarkerResponse"];

export interface MarkerDisplayOption {
  label: string;
  value: string;
}

async function fetchMarkers(scope: {
  agentId?: string;
  teamId?: string;
}): Promise<MarkerResponse[]> {
  const markers: MarkerResponse[] = [];
  let pageToken: string | undefined;
  do {
    const { data, error } = await apiClient.GET("/v1/markers", {
      params: {
        query: {
          agent_id: scope.agentId,
          page_size: MAX_PAGE_SIZE,
          page_token: pageToken,
          team_id: scope.teamId,
        },
      },
    });
    if (error) {
      throw new ApiRequestError(error);
    }
    markers.push(...data.items);
    pageToken = data.next_page_token ?? undefined;
  } while (pageToken);
  return markers;
}

export function useDashboardMarkerNames(agentId: string | undefined): {
  displayNames: ReadonlyMap<string, string>;
  options: readonly MarkerDisplayOption[];
} {
  const workingTeamId = useResolvedWorkingTeamId();
  const filters =
    agentId != null ? { agent_id: agentId } : { team_id: workingTeamId };
  const markers = useQuery({
    enabled: agentId != null || workingTeamId !== undefined,
    queryKey: queryKeys.markers.list({
      ...filters,
      view: "dashboard-display-names",
    }),
    queryFn: () =>
      fetchMarkers(agentId != null ? { agentId } : { teamId: workingTeamId }),
  });

  return useMemo(() => {
    const displayNames = new Map<string, string>();
    const options = (markers.data ?? [])
      .map((marker) => {
        const displayName = marker.current_revision.display_name;
        displayNames.set(marker.id, displayName);
        return { label: displayName, value: marker.id };
      })
      .sort((first, second) => first.label.localeCompare(second.label));
    return { displayNames, options };
  }, [markers.data]);
}
